import Link from 'next/link';
import { useTranslation } from 'next-export-i18n';
import React from 'react';

import EmptyStageIcon from '@/assets/empty-stage.svg';
import SimpleCard from '@/components/cards/Card';
import ErrorStage from '@/components/stage/ErrorStage';
import type CompanyProps from '@/types/Company.type';
import { logCustomEvent } from '@/utils/analytic';
import { AnalyticEvent } from '@/utils/enum';
import { getCleanLink, getLocationDisplay, isValidLink } from '@/utils/helper';

type VenturesProps = {
  company: CompanyProps;
};

const Ventures = ({ company }: VenturesProps) => {
  const { t } = useTranslation();
  const count = company.ventures.length;

  return (
    <SimpleCard padding="p-5">
      <h2 className="font-bold">
        {t('company.venture', { plural: count > 1 ? 's' : '' })} ({count})
      </h2>
      {count <= 0 && (
        <ErrorStage
          title={t('stage.empty.title', { type: 'venture' })}
          errorIcon={EmptyStageIcon}
          imageClassName="opacity-50"
        />
      )}
      <table className="venture-table mt-3 w-full table-fixed md:mt-7">
        {count > 0 && (
          <thead>
            <tr>
              <th>{t('company.name')}</th>
              <th>{t('company.location', { plural: '' })}</th>
              <th className="hidden sm:block">{t('company.website')}</th>
            </tr>
          </thead>
        )}
        <tbody>
          {company.ventures.map(({ name, country, city, website }, index) => {
            return (
              <tr key={index}>
                <td className="font-medium">
                  <div className="sm:hidden">
                    {isValidLink(website) ? (
                      <Link href={getCleanLink(website)}>
                        <a
                          href={getCleanLink(website)}
                          target="_blank"
                          className="text-center text-base text-blue-500"
                          rel="noreferrer"
                        >
                          <span>{name}</span>
                        </a>
                      </Link>
                    ) : (
                      name
                    )}
                  </div>

                  <span className="hidden sm:block">{name}</span>
                </td>
                <td className="capitalize">
                  {getLocationDisplay(country, city)}
                </td>
                <td className="hidden sm:block">
                  {isValidLink(website) ? (
                    <Link href={getCleanLink(website)}>
                      <a
                        href={getCleanLink(website)}
                        target="_blank"
                        className="text-center text-base text-blue-500"
                        rel="noreferrer"
                        onClick={() =>
                          logCustomEvent(
                            AnalyticEvent.ViewCompanyVentureWebsite,
                            {
                              venture: JSON.stringify({
                                name,
                                website,
                              }),
                            }
                          )
                        }
                      >
                        <span>{website}</span>
                      </a>
                    </Link>
                  ) : (
                    'N/A'
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </SimpleCard>
  );
};

export default Ventures;
